import {
  type ReactNode,
  type Ref,
  forwardRef,
  useImperativeHandle,
} from "react"
import { useTranslation } from "react-i18next"

import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
  useMessageScroller,
  useMessageScrollerScrollable,
} from "@/components/ui/message-scroller"
import { cn } from "@/lib/utils"
import type { ThreadMessage } from "@/types/messaging"

import { ThreadMessageBubble } from "./thread-message-bubble"

/**
 * OKS-654 — Shared message-list region built on the shadcn
 * `MessageScroller` primitive. Renders both the brand `ThreadPane`
 * and the `Creator/Messages/Index` thread on one substrate so
 * alignment, date separators, auto-scroll and a11y stay identical
 * across sides.
 *
 * Behaviour:
 *   - `autoScroll` sticks the viewport to the newest message on mount;
 *     the down-arrow `MessageScrollerButton` appears when the user
 *     scrolls up.
 *   - OKS-819 — the stick-to-newest on a 30s-poll re-render is GATED on
 *     the reader being at/near the bottom (see `ThreadItems`). A reader
 *     who scrolled up to read history is NOT yanked down when a polled
 *     message arrives; a reader already at the bottom still follows.
 *   - `[content-visibility:auto]` on each item virtualises long
 *     threads (UX Spec §3 edge — "very long thread scrolls; date
 *     separators persist").
 *
 * Accessibility (UX Spec §6, preserved through the OKS-654 swap):
 *   - The viewport is `role="log"` `aria-live="polite"` so AT
 *     announces polled arrivals; reading order stays oldest→newest.
 */
interface MessageThreadScrollerProps {
  messages: ThreadMessage[]
  /** Accessible name for the log region. */
  ariaLabel: string
  /** Preserved test id for the scroll region (e.g. `messaging-thread-messages`). */
  testId: string
  className?: string
}

/**
 * OKS-724 — imperative handle a parent surface (Creator `Messages/Index`,
 * brand `ThreadPane`) holds via `ref` to force the thread to the newest
 * message. Fired ONLY from the composer's successful own-send — NOT on the
 * declarative `autoScroll`/`scrollAnchor` path (that stays the poll's
 * stick-to-bottom-only-if-already-bottom behaviour, unchanged).
 */
export interface MessageThreadScrollerHandle {
  /** Force the thread viewport to the newest message (instant jump). */
  scrollToNewest: () => void
}

export const MessageThreadScroller = forwardRef<
  MessageThreadScrollerHandle,
  MessageThreadScrollerProps
>(function MessageThreadScroller(
  { messages, ariaLabel, testId, className },
  ref,
) {
  return (
    <MessageScrollerProvider
      autoScroll
      defaultScrollPosition="end"
      // OKS-819 — tolerance band for "at/near bottom": within 96px of the
      // bottom (≈ one message row) still counts as at-bottom, so a reader a
      // hair off the bottom is still followed and never stranded one line up.
      // Governs when `useMessageScrollerScrollable().end` flips (see
      // `ThreadItems`).
      scrollEdgeThreshold={96}
    >
      {/* Bridge lives INSIDE the provider — the only place `useMessageScroller`
          is valid — and forwards the parent's ref up as the imperative
          handle. Renders nothing. */}
      <ScrollToNewestBridge handleRef={ref} />
      <MessageScroller className={cn("size-full min-h-0", className)}>
        <MessageScrollerViewport
          role="log"
          aria-live="polite"
          aria-label={ariaLabel}
          data-testid={testId}
        >
          <MessageScrollerContent className="gap-0 p-3">
            {/* Provider-scoped so it can read the primitive's at-bottom
                state via `useMessageScrollerScrollable` (OKS-819 gate). */}
            <ThreadItems messages={messages} />
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton />
      </MessageScroller>
    </MessageScrollerProvider>
  )
})

/**
 * OKS-724 — provider-scoped bridge that exposes `scrollToNewest` on the
 * forwarded ref by calling the shadcn primitive's own `scrollToEnd`
 * (`useMessageScroller`) — NOT a raw DOM `scrollIntoView`, so the primitive's
 * anchor/virtualisation math stays authoritative and the `/ui` primitive is
 * left un-edited.
 *
 * `scrollToEnd` moves the internal `MessageScrollerViewport` (the
 * `overflow-y-auto` `role="log"` node), NOT the window — so the composer's
 * `preserveScroll: true` window position and the OKS-723 `shrink-0` composer
 * band (a sibling OUTSIDE the viewport) are both untouched.
 */
function ScrollToNewestBridge({
  handleRef,
}: {
  handleRef: Ref<MessageThreadScrollerHandle>
}) {
  const { scrollToEnd } = useMessageScroller()
  useImperativeHandle(
    handleRef,
    () => ({
      scrollToNewest() {
        // Defer one frame: the composer's `onSuccess` fires after Inertia
        // swaps props, but React may not have flushed the new bubble into the
        // DOM yet. A single rAF guarantees the newest `MessageScrollerItem`
        // exists before we scroll to it. `instant` (no smooth animation) keeps
        // the end-state deterministic on this known-flaky surface.
        requestAnimationFrame(() => {
          scrollToEnd({ behavior: "instant", align: "end" })
        })
      },
    }),
    [scrollToEnd],
  )
  return null
}

/**
 * OKS-819 — provider-scoped message list. Interleaves day-boundary
 * date-separator chips between messages, wrapping each row in a
 * `MessageScrollerItem` (ASC-by-`createdAt`).
 *
 * The at-bottom gate: `useMessageScrollerScrollable().end` is true while
 * the viewport can still scroll toward the end — i.e. the reader has
 * scrolled UP to read history; it is false ⟺ the reader is at/near the
 * bottom (within the provider's `scrollEdgeThreshold`, 96px). The final
 * message carries `scrollAnchor` ONLY when at bottom, so the primitive
 * re-pins to newest on a 30s-poll re-render only for a reader already at
 * the bottom — a scrolled-up reader stays put (the OKS-819 fix).
 *
 * This declarative gate is SEPARATE from the imperative own-send
 * `ScrollToNewestBridge`, which force-scrolls to newest from ANY position
 * (OKS-724) and is intentionally left un-gated.
 *
 * Must render inside `MessageScrollerProvider` — the hook is only valid
 * there.
 */
function ThreadItems({ messages }: { messages: ThreadMessage[] }) {
  const { end } = useMessageScrollerScrollable()
  const atBottom = !end

  const out: ReactNode[] = []
  let lastDayKey: string | null = null
  const lastId = messages[messages.length - 1]?.id

  for (const m of messages) {
    const day = dayKey(m.createdAt)
    if (day !== lastDayKey) {
      out.push(
        <MessageScrollerItem key={`sep-${day}`} className="my-2">
          <DateSeparator iso={m.createdAt} />
        </MessageScrollerItem>,
      )
      lastDayKey = day
    }
    out.push(
      <MessageScrollerItem
        key={m.id}
        messageId={String(m.id)}
        scrollAnchor={atBottom && m.id === lastId}
        className="py-1"
      >
        <ThreadMessageBubble message={m} />
      </MessageScrollerItem>,
    )
  }

  return (
    <>
      {/* OKS-819 — hidden gate-decision sentinel for E2E: reflects whether
          the at-bottom gate is ARMED (`true` → poll follows to newest) or
          SUPPRESSED (`false` → scrolled-up reader held). Lets a scroll spec
          assert the gate DECISION directly rather than inferring it from
          resulting `scrollTop`. `hidden` → zero layout/visual impact. */}
      <div
        data-testid="messaging-thread-scroll-gate"
        data-at-bottom={atBottom}
        hidden
      />
      {out}
    </>
  )
}

function dayKey(iso: string): string {
  return iso.slice(0, 10)
}

function DateSeparator({ iso }: { iso: string }) {
  const { t } = useTranslation()
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  let label: string
  if (sameDay(d, today)) {
    label = t("messaging.thread.date.today")
  } else if (sameDay(d, yesterday)) {
    label = t("messaging.thread.date.yesterday")
  } else {
    // Locale-aware long-form date for prior days.
    label = d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  return (
    <div className="flex items-center justify-center">
      <span
        className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase"
        data-testid="messaging-thread-date-separator"
      >
        {label}
      </span>
    </div>
  )
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}
